import { FC } from 'react';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import useResetPassword from '@/api/useResetPassword.ts';
import { passwordSchema } from '$/utils/validators/passwordSchema.ts';
import { TextField, Typography, Box, Stack } from '@mui/material';

const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: 'This field is required.' }),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'The passwords did not match',
        path: ['repeat_password'],
      });
    }
  });

type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>;

const ResetPasswordPage: FC = () => {
  const { t } = useTranslation();
  const {
    mutateAsync: resetPassword,
    isPending: isLoadingResetPassword,
    error: errorResetPassword,
    isError: isErrorResetPassword,
    isSuccess: isSuccessResetPassword,
  } = useResetPassword();

  const { control, handleSubmit } = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      repeat_password: '',
    },
  });

  const handleResetPassword = (data: ResetPasswordSchema) => {
    void (async () => {
      try {
        if (isLoadingResetPassword) return;

        await resetPassword({
          password: data.password,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  if (isSuccessResetPassword) {
    return (
      <BrandCardSideContent className="w-screen h-screen bg-bg-light px-4 py-6">
        <Stack spacing={4} sx={{ maxWidth: '100%', textAlign: 'center' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
              {t('resetPasswordPage.header')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('resetPasswordPage.successInfo')}
            </Typography>
          </Box>
          <Link to={Routes.LOGIN}>
            <Button
              color="primary"
              className="btn-primary w-full rounded-full normal-case bg-[#4E7DF6] hover:bg-[#3D63C4] border-none"
            >
              {t('button.backToLogin')}
            </Button>
          </Link>
        </Stack>
      </BrandCardSideContent>
    );
  }

  return (
    <BrandCardSideContent className="w-screen h-screen bg-bg-light px-4 py-6">
      <Stack spacing={4} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {t('resetPasswordPage.header')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('resetPasswordPage.description')}
          </Typography>
        </Box>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(handleResetPassword)}>
          <Stack spacing={3}>
            {isErrorResetPassword && <Alert status="error">{errorResetPassword.message}</Alert>}
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  value={value}
                  onChange={onChange}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('placeholder.newPassword')}
                  variant="outlined"
                />
              )}
            />
            <Controller
              control={control}
              name="repeat_password"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.repeat_password}
                  helperText={errors.repeat_password?.message}
                  value={value}
                  onChange={onChange}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('placeholder.repeat_password')}
                  variant="outlined"
                />
              )}
            />
            <Button
              color="primary"
              className="btn-primary w-full rounded-full normal-case bg-[#4E7DF6] hover:bg-[#3D63C4] border-none"
              type="submit"
              disabled={isLoadingResetPassword}
            >
              {t('button.resetPassword')}
            </Button>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Link to={Routes.LOGIN}>
              <Button color="ghost">{t('button.backToLogin')}</Button>
            </Link>
          </Box>
        </form>
      </Stack>
    </BrandCardSideContent>
  );
};

export default ResetPasswordPage;
